import type { Metal, StoreSort } from "./api-client";

// /store URLs (plan 2.13, F78): one facet level per path, never combinations.
//   /store · /store/gold · /store/gold/1g · /store/gold/24k · /store/brand/arses
// Any further facet, the sort and the page go in the query string; those
// views are noindex with the canonical on the clean path (page 2+ of a clean
// path is self-canonical). This is the one place they're built, so the server
// pagination links and the infinite scroll's fallback link always agree.

export interface StoreView {
  metal?: Metal;
  weight?: string;
  fineness?: string;
  brand?: string;
}

export interface StoreState extends StoreView {
  sort: StoreSort;
  page: number;
}

export const METALS: Metal[] = ["gold", "silver"];

/** The path part of a view, and which facets it carries. */
function pathOf(view: StoreView): { path: string; inPath: (keyof StoreView)[] } {
  if (view.metal && view.weight) return { path: `/store/${view.metal}/${view.weight}`, inPath: ["metal", "weight"] };
  if (view.metal && view.fineness) return { path: `/store/${view.metal}/${view.fineness}`, inPath: ["metal", "fineness"] };
  if (view.metal) return { path: `/store/${view.metal}`, inPath: ["metal"] };
  if (view.brand) return { path: `/store/brand/${view.brand}`, inPath: ["brand"] };
  return { path: "/store", inPath: [] };
}

export function storeHref(state: Partial<StoreState> & StoreView) {
  const { path, inPath } = pathOf(state);
  const params = new URLSearchParams();
  for (const key of ["weight", "fineness", "brand"] as const) {
    const value = state[key];
    if (value && !inPath.includes(key)) params.set(key, value);
  }
  if (state.sort && state.sort !== "default") params.set("sort", state.sort);
  if (state.page && state.page > 1) params.set("page", String(state.page));
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** The clean path of a view (its canonical), and whether the state adds query-only filters. */
export function canonicalOf(state: StoreState) {
  const { path, inPath } = pathOf(state);
  const extraFilters = (["weight", "fineness", "brand"] as const).some((k) => state[k] && !inPath.includes(k));
  const filtered = extraFilters || state.sort !== "default";
  return { canonical: filtered ? path : storeHref({ ...pick(state, inPath), page: state.page }), indexable: !filtered };
}

function pick(view: StoreView, keys: (keyof StoreView)[]): StoreView {
  return Object.fromEntries(keys.map((k) => [k, view[k]])) as StoreView;
}
